import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/marketing/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/marketing/components/ui/dialog";
import type { CampaignTrackerAudit } from "@/marketing/models/campaignTracker";
import { TRACKER_QUERIES } from "@/marketing/infrastructure/influencer/campaign/tracker/queries";

const ACTIONS: Record<CampaignTrackerAudit["action"], string> = {
  metadataUpdated: "Campaign settings updated",
  groupCreated: "Group created",
  groupUpdated: "Group updated",
  membershipsUpdated: "Creator groups updated",
};

function flatten(value: unknown, path = ""): Record<string, unknown> {
  if (value == null && !path) return {};
  if (Array.isArray(value))
    return Object.fromEntries(
      value.flatMap((entry, index) => {
        const id = entry !== null && typeof entry === "object" && "influencerId" in entry ? String(entry.influencerId) : String(index + 1);
        return Object.entries(flatten(entry, path ? `${path}.${id}` : id));
      }),
    );
  if (value !== null && typeof value === "object" && !Array.isArray(value))
    return Object.fromEntries(
      Object.entries(value).flatMap(([key, entry]) => Object.entries(flatten(entry, path ? `${path}.${key}` : key))),
    );
  return { [path || "value"]: value };
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "Not set";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function HistoryChanges({ entry }: { entry: CampaignTrackerAudit }) {
  const before = flatten(entry.before);
  const after = flatten(entry.after);
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
    (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
  );
  return (
    <dl className="tw-mt-2 tw-space-y-1.5">
      {keys.map((key) => (
        <div key={key} className="tw-rounded-md tw-bg-muted/50 tw-px-2.5 tw-py-2 tw-text-xs">
          <dt className="tw-font-medium tw-capitalize">{key.replace(/\./g, " · ").replace(/([A-Z])/g, " $1")}</dt>
          <dd className="tw-mt-1 tw-break-words tw-text-muted-foreground">
            <span className="tw-line-through">{displayValue(before[key])}</span>
            <span className="tw-mx-2">→</span>
            <span className="tw-text-foreground">{displayValue(after[key])}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function TrackerHistory({ campaignId, campaignName, onClose }: { campaignId: string; campaignName: string; onClose: () => void }) {
  const [offset, setOffset] = useState(0);
  const query = useQuery(TRACKER_QUERIES.history(campaignId, offset));
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="tw-flex tw-max-h-[85vh] tw-max-w-2xl tw-flex-col">
        <DialogHeader>
          <DialogTitle>Change history</DialogTitle>
          <DialogDescription>{campaignName} · Times in Asia/Seoul. Editor labels are client reported.</DialogDescription>
        </DialogHeader>
        <div className="tw-min-h-0 tw-flex-1 tw-space-y-4 tw-overflow-y-auto">
          {query.isPending && <p className="tw-py-6 tw-text-sm tw-text-muted-foreground">Loading change history…</p>}
          {query.isError && (
            <div role="alert">
              <p className="tw-mb-2 tw-text-sm">Could not load history.</p>
              <Button variant="outline" onClick={() => void query.refetch()}>
                Retry
              </Button>
            </div>
          )}
          {query.data?.entries.length === 0 && (
            <p className="tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground">No tracker changes recorded yet.</p>
          )}
          {query.data?.entries.map((entry) => (
            <article key={entry.logId} className="tw-rounded-lg tw-border tw-border-border tw-p-4">
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-text-sm">
                <h3 className="tw-font-semibold">{ACTIONS[entry.action]}</h3>
                <span className="tw-shrink-0 tw-text-xs tw-text-muted-foreground">Version {entry.version}</span>
              </div>
              <p className="tw-mt-1 tw-text-xs tw-text-muted-foreground">
                {entry.clientReportedActor ?? "No editor label"} ·{" "}
                {new Date(entry.createdAt).toLocaleString("en-US", { timeZone: "Asia/Seoul" })}
              </p>
              <HistoryChanges entry={entry} />
            </article>
          ))}
        </div>
        {query.data && query.data.total > 50 && (
          <div className="tw-flex tw-items-center tw-justify-between tw-border-t tw-border-border tw-pt-3">
            <Button variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>
              Previous
            </Button>
            <span className="tw-text-xs tw-text-muted-foreground">
              {offset + 1}–{offset + query.data.entries.length} of {query.data.total}
            </span>
            <Button
              variant="outline"
              disabled={offset + query.data.entries.length >= query.data.total}
              onClick={() => setOffset(offset + 50)}
            >
              Next
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
